import type { AxiosInstance } from 'axios';
import type {
  AffiliatePushState, BusinessStats, CreateUserRequest, CyclingEvent, DailyRegistrationCount, FavoriteAccounts,
  UpdateUserRequest, User,
  UserStats, UserStatsBucket, YearOverYearReport,
} from '../types';
import { LINK_TYPE_LABELS } from '../brand';

/**
 * Abrufe der Verwaltung, Bereich Auswertung: Suche & Besucher, Klicks, Geschäftszahlen, Beliebteste, Event-Übersicht, Nutzer.
 * Einmal für Web und App; die App ruft sie über `create…Service(api)` mit ihrer eigenen Axios-Instanz.
 * Typen, die es in `types.ts` noch nicht gibt, stehen hier im Bereich.
 *
 * Gerechnet wird im Backend. Was hier steht, sind Abrufe, Beschriftungen und das Auffüllen von
 * Tagesreihen für Diagramme, keine zweite Rechnung über dieselben Zahlen.
 *
 * Herkunft: die Typen der Suchmaschinen-Auswertung und der Klick-Statistiken stehen bisher im Web
 * (`frontend/src/services/seoAnalyticsService.ts`, `trackingService.ts`), die Tagesreihe der
 * Nutzerzahlen in `frontend/src/utils/userStats.ts`. Sie liegen hier mit dem Präfix `Seo`/`Klick`,
 * damit sie neben den Namen der anderen Bereiche bestehen; das Web kann sie beim nächsten Anfassen
 * von hier nehmen.
 */

// ── Suche & Besucher (/admin/seo-analytics) ────────────────────────────────────────────────

export type SeoRunStatus = 'OK' | 'EMPTY' | 'ERROR' | 'DISABLED';
export type SeoTrafficChannel = 'GOOGLE' | 'OTHER_SEARCH' | 'AI_CHAT' | 'SOCIAL' | 'DIRECT' | 'REFERRAL';

export const SEO_CHANNEL_LABELS: Record<SeoTrafficChannel, string> = {
  GOOGLE: 'Google-Suche',
  OTHER_SEARCH: 'Andere Suchmaschinen',
  AI_CHAT: 'KI-Chats',
  SOCIAL: 'Social Media',
  DIRECT: 'Direkt eingegeben',
  REFERRAL: 'Verlinkt von anderen Seiten',
};

export const SEO_SOURCE_LABELS: Record<string, string> = {
  gsc: 'Google Search Console',
  umami: 'Besucherstatistik',
};

export interface SeoSourceStatus {
  source: string;
  configured: boolean;
  status: SeoRunStatus | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  message: string | null;
  rowsWritten: number;
  dataFrom: string | null;
  dataUntil: string | null;
}

export interface SeoPeriodTotals {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
  visitors: number;
  pageviews: number;
}

export interface SeoPeriodPoint extends SeoPeriodTotals {
  label: string;
  start: string;
  end: string;
  botSessions: number | null;
  botSessionsTotal: number | null;
  previousYear: SeoPeriodTotals | null;
  /** Falsch, solange der Zeitraum vor dem Messbruch liegt. */
  visitorsComparable: boolean;
}

export interface SeoHeadline {
  from: string;
  to: string;
  current: SeoPeriodTotals;
  previousPeriod: SeoPeriodTotals;
  previousYear: SeoPeriodTotals;
  previousPeriodFrom: string;
  previousPeriodTo: string;
  previousYearFrom: string;
  previousYearTo: string;
  visitorsComparable: boolean;
}

export interface SeoChannelShare {
  channel: SeoTrafficChannel;
  visitors: number;
  share: number;
}

export interface SeoChannels {
  from: string;
  to: string;
  current: SeoChannelShare[];
  previous: SeoChannelShare[];
  classifiedVisitors: number;
  totalVisitors: number;
  unattributedVisitors: number;
}

export interface SeoReconciliation {
  from: string;
  to: string;
  searchConsoleClicks: number;
  measuredGoogleVisitors: number;
  difference: number;
  deviation: number | null;
}

export interface SeoBots {
  from: string;
  to: string;
  botSessions: number;
  sessions: number;
  share: number | null;
  coveredDays: number;
  totalDays: number;
}

export interface SeoAggregateRow {
  key: string;
  clicks: number;
  impressions: number;
  position: number | null;
  ctr: number;
}

export interface SeoMigrationPoint {
  label: string;
  start: string;
  end: string;
  legacyClicks: number;
  totalClicks: number;
  share: number;
}

export interface SeoMigration {
  from: string;
  to: string;
  legacyClicks: number;
  totalClicks: number;
  share: number;
  currentWeekShare: number | null;
  trend: SeoMigrationPoint[];
  topLegacyPages: SeoAggregateRow[];
}

export interface SeoAiOverview {
  from: string;
  to: string;
  hasAppearanceData: boolean;
  aiImpressions: number;
  aiClicks: number;
  totalImpressions: number;
  share: number | null;
  appearances: SeoAggregateRow[];
}

export interface SeoOverview {
  searchConsole: SeoSourceStatus;
  visitors: SeoSourceStatus;
  measurementBreak: string | null;
  headline: SeoHeadline;
  weekly: SeoPeriodPoint[];
  monthly: SeoPeriodPoint[];
  channels: SeoChannels;
  reconciliation: SeoReconciliation;
  bots: SeoBots;
  migration: SeoMigration;
  aiOverview: SeoAiOverview;
  collecting: boolean;
}

export type SeoLandingPageKind =
  | 'EVENT' | 'PRO_RACE' | 'NEWS' | 'ORGANIZER' | 'UEBERSICHT' | 'STARTSEITE' | 'ALTE_ADRESSE' | 'SONSTIGE';

export const SEO_LANDING_PAGE_LABELS: Record<SeoLandingPageKind, string> = {
  EVENT: 'Event-Seite',
  PRO_RACE: 'Profi-Rennen',
  NEWS: 'News',
  ORGANIZER: 'Veranstalter',
  UEBERSICHT: 'Übersicht',
  STARTSEITE: 'Startseite',
  ALTE_ADRESSE: 'alte Adresse ohne Ziel',
  SONSTIGE: 'sonstige',
};

export interface SeoOpportunityRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
  expectedCtr: number;
  missedClicks: number;
  topPage: string | null;
  topPageKind: SeoLandingPageKind | null;
  viaRedirect: boolean;
  resolvedPage: string | null;
  eventTitle: string | null;
  eventDate: string | null;
  eventOver: boolean;
}

export interface SeoOpportunities {
  from: string;
  to: string;
  rows: SeoOpportunityRow[];
}

export interface SeoSitemapGapRow {
  page: string;
  clicks: number;
  impressions: number;
  position: number | null;
}

export interface SeoSitemapGap {
  from: string;
  to: string;
  sitemapUrls: number;
  checkedPages: number;
  missing: SeoSitemapGapRow[];
  ignoredLegacyPages: number;
}

// ── Klick-Statistiken (/admin/stats/tracking) ──────────────────────────────────────────────

export interface KlickKennzahlen {
  impressions: number;
  clicks: number;
  views: number;
  downloads: number;
  uniqueVisitors: number;
  uniqueClickers: number;
}

export interface KlickZiel extends KlickKennzahlen {
  context: string | null;
}

export interface KlickEintrag extends KlickKennzahlen {
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  entityDate: string | null;
  /** Personen zählen über die ganze Entität, die Zeile ist deshalb nicht die Summe ihrer Ziele. */
  breakdown: KlickZiel[];
}

export const KLICK_TYP_LABELS: Record<string, string> = {
  AD_BANNER: 'Werbebanner',
  NEWS: 'News-Artikel',
  EVENT: 'Events',
  PRO_RACE_STAGE: 'Etappen',
  PAGE: 'Seiten',
};

export const KLICK_TYP_REIHENFOLGE = ['EVENT', 'NEWS', 'AD_BANNER', 'PRO_RACE_STAGE', 'PAGE'];

/** Dieselben Wörter wie in `TrackingStats.tsx` im Web. */
const KLICK_ZIEL_LABELS: Record<string, string> = {
  anmeldung: 'Anmeldung',
  landing: 'Eingeblendet',
  'landing-desktop': 'Geklickt am Desktop',
  'landing-mobile': 'Geklickt am Handy',
  instagram: 'Geklickt: Instagram',
  facebook: 'Geklickt: Facebook',
  ausschreibung: 'Ausschreibung',
  'veranstalter-seite': 'Veranstalter-Seite',
  ical: 'Kalender-Download',
  unterkunft: 'Unterkunft',
  'news-lead': 'Teaser (Aufmacher)',
  'news-grid': 'Teaser (Liste)',
  'news-related': 'Aus anderem Artikel',
  'datei-standard': 'Logo geladen (Standard)',
  'datei-klein': 'Logo geladen (klein)',
  'datei-standard-hell': 'Logo geladen (Standard, hell)',
  'datei-klein-hell': 'Logo geladen (klein, hell)',
  'baustein-html': 'Baustein kopiert (HTML)',
  'baustein-textlink': 'Baustein kopiert (Textlink)',
  'baustein-markdown': 'Baustein kopiert (Markdown)',
};

export function klickZielLabel(context: string | null): string {
  if (!context) return 'Seitenaufruf';
  if (KLICK_ZIEL_LABELS[context]) return KLICK_ZIEL_LABELS[context];
  if (context.startsWith('link:')) return `Link: ${LINK_TYPE_LABELS[context.slice(5)] ?? context.slice(5)}`;
  return context;
}

/** Werbeplätze tragen keinen Titel; Namen wie `adBannerLabel` im Web (auch frühere Anzeigen). */
const WERBE_LABELS: Record<string, string> = {
  'delta-bike': 'Delta Bike (Equipment-Partner)',
  'organizer-promo': 'Für Veranstalter („Mehr Leute am Start“)',
  'partner-promo': 'Hier werben („Deine Marke. Mitten im Radsport“)',
  'tdf-promo': 'Tour de France (Rennkalender)',
  'social-promo': 'Social Media (Instagram und Facebook)',
};

export const werbeplatzLabel = (key: string): string => WERBE_LABELS[key] ?? key;

// ── Geschäftszahlen ────────────────────────────────────────────────────────────────────────

/** Die Stufen des Kaufwegs mit der Handlung dahinter, wie `BusinessStats.tsx`. */
export const FUNNEL_STUFEN: Record<string, { titel: string; detail: string }> = {
  offer_viewed: { titel: 'Angebot angesehen', detail: 'Seite „Für Veranstalter“ geöffnet' },
  offer_selected: { titel: 'Angebot gewählt', detail: 'Kostenlos, Premium oder Hervorhebung geklickt' },
  organizer_selected: { titel: 'Veranstalter gewählt', detail: 'im Verzeichnis den eigenen Verein geöffnet' },
  organizer_registration_submitted: { titel: 'Veranstalter eingetragen', detail: 'der Verein fehlte im Verzeichnis' },
  claim_started: { titel: 'Zugang begonnen', detail: 'Antrag auf der Veranstalterseite geöffnet' },
  claim_completed: { titel: 'Zugang beantragt', detail: 'abgeschickt, sofort aktiv oder zur Prüfung' },
  premium_checkout_started: { titel: 'Premium begonnen', detail: 'Premium-Fenster geöffnet' },
  premium_requested: { titel: 'Premium bestellt', detail: 'Anfrage abgeschickt' },
  promotion_checkout_started: { titel: 'Hervorhebung begonnen', detail: 'Buchungsfenster geöffnet' },
  promotion_requested: { titel: 'Hervorhebung bestellt', detail: 'Anfrage abgeschickt' },
};

export const funnelStufe = (step: string) => FUNNEL_STUFEN[step]?.titel ?? step;

// ── Event-Übersicht ────────────────────────────────────────────────────────────────────────

/** Feldnamen im Audit-Log, wie `utils/fieldLabels.ts` im Web. */
export const AUSWERTUNG_FELD_LABELS: Record<string, string> = {
  title: 'Titel', eventDate: 'Datum', status: 'Status', description: 'Beschreibung', distances: 'Distanzen', komootCollectionUrl: 'Komoot-Link',
  gpxPath: 'GPX-Datei', flyerUrl: 'Flyer', organizer: 'Veranstalter', organizerId: 'Veranstalter',
  organizerUrl: 'Veranstaltungs-URL', organizerLogoUrl: 'Veranstalter-Logo', startTime: 'Startzeit',
  startTimeEnd: 'Startzeit (Ende)', participants: 'Teilnehmer (letzte Auflage)', priceTiers: 'Preisstufen',
  latitude: 'Koordinaten', longitude: 'Koordinaten', locationName: 'Ort', street: 'Straße',
  houseNumber: 'Hausnummer', postalCode: 'PLZ', city: 'Stadt', bundesland: 'Bundesland',
  eventType: 'Event-Typ', category: 'Kategorie', fullyBooked: 'Ausgebucht', cancelled: 'Abgesagt',
  tentative: 'Vorläufig', registrationStart: 'Anmeldestart', registrationMode: 'Anmeldeart',
  registrationState: 'Anmeldestand', links: 'Links', additionalLinks: 'Links',
  // Felder der Veranstalter- und Profi-Rennen-Enricher.
  email: 'E-Mail', phone: 'Telefon', website: 'Website', logoUrl: 'Logo', registrationUrl: 'Anmelde-Link',
  imageUrl: 'Bild', bannerUrl: 'Banner', instagramUrl: 'Instagram', facebookUrl: 'Facebook',
};

/** Technische Felder: ein Eintrag, der nur sie berührt, fällt aus dem Verlauf. */
export const AUSWERTUNG_RAUSCH_FELDER = new Set<string>([
  'externalId', 'sourceUrl', 'source', 'lastSeenAt', 'firstSeenAt', 'updatedAt', 'createdAt', 'active',
]);

export const feldLabel = (feld: string): string => AUSWERTUNG_FELD_LABELS[feld] ?? feld;

// ── Nutzer ─────────────────────────────────────────────────────────────────────────────────

export const LEERER_NUTZER_STAND: UserStatsBucket = {
  total: 0, active: 0, dormant: 0, inactive: 0, registrationsPerDay: [],
};

/** „Alle" ist die Summe beider Bestätigungsstände; Tagesreihen je Tag addiert (wie `utils/userStats.ts`). */
export function nutzerStaendeZusammen(a: UserStatsBucket, b: UserStatsBucket): UserStatsBucket {
  const jeTag: Record<string, number> = {};
  for (const r of a.registrationsPerDay.concat(b.registrationsPerDay)) jeTag[r.day] = (jeTag[r.day] || 0) + r.count;
  return {
    total: a.total + b.total,
    active: a.active + b.active,
    dormant: a.dormant + b.dormant,
    inactive: a.inactive + b.inactive,
    registrationsPerDay: Object.keys(jeTag).sort().map(day => ({ day, count: jeTag[day] })),
  };
}

const TAG_MS = 86_400_000;

/**
 * Ein Wert je UTC-Kalendertag, Tage ohne Anmeldung als 0 (sonst stünden zwei Monate Abstand als
 * Nachbarbalken nebeneinander). `tage = 0`: seit dem ersten Eintrag.
 */
export function tagesreihe(rows: DailyRegistrationCount[], tage: number): DailyRegistrationCount[] {
  if (rows.length === 0) return [];
  const jeTag: Record<string, number> = {};
  for (const r of rows) jeTag[r.day] = r.count;
  const letzter = Math.floor(Date.now() / TAG_MS) * TAG_MS;
  const erster = tage > 0 ? letzter - (tage - 1) * TAG_MS : Math.floor(new Date(rows[0].day + 'T00:00:00Z').getTime() / TAG_MS) * TAG_MS;
  const aus: DailyRegistrationCount[] = [];
  for (let t = Math.min(erster, letzter); t <= letzter; t += TAG_MS) {
    const day = new Date(t).toISOString().slice(0, 10);
    aus.push({ day, count: jeTag[day] || 0 });
  }
  return aus;
}

export function createAuswertungService(api: AxiosInstance) {
  const SEO = '/admin/seo-analytics';
  return {
    // Suche & Besucher: Admin und Moderator; nur `seoAbholen` ist Admin allein.
    seoUebersicht: async (days: number): Promise<SeoOverview> =>
      (await api.get<SeoOverview>(`${SEO}/overview`, { params: { days } })).data,
    seoChancen: async (includePastEvents: boolean): Promise<SeoOpportunities> =>
      (await api.get<SeoOpportunities>(`${SEO}/opportunities`, { params: { includePastEvents } })).data,
    seoSitemapLuecken: async (): Promise<SeoSitemapGap> =>
      (await api.get<SeoSitemapGap>(`${SEO}/sitemap-gap`)).data,
    seoVorjahr: async (): Promise<YearOverYearReport> =>
      (await api.get<YearOverYearReport>(`${SEO}/movers/year-over-year`)).data,
    seoAbholen: async (): Promise<{ started: boolean; message: string }> =>
      (await api.post<{ started: boolean; message: string }>(`${SEO}/collect`)).data,

    /** Klick-Statistiken, Admin und Moderator. Datum als `JJJJ-MM-TT`. */
    klicks: async (from: string, to: string): Promise<KlickEintrag[]> =>
      (await api.get<KlickEintrag[]>('/admin/stats/tracking', { params: { from, to } })).data,

    /** Nur ADMIN: Umsatz und Verkaufsliste. */
    geschaeftszahlen: async (days: number): Promise<BusinessStats> =>
      (await api.get<BusinessStats>('/admin/business-stats', { params: { days } })).data,

    /** Nur ADMIN: CJ und Awin jetzt abfragen. Läuft im Hintergrund; `running` = lief schon. */
    partnerprogrammeAbrufen: async (): Promise<{ running: boolean }> =>
      (await api.post<{ running: boolean }>('/admin/business-stats/affiliate/collect')).data,

    /** Nur ADMIN: Push „Neue Provision" für das eigene Konto. */
    partnerPush: async (): Promise<AffiliatePushState> =>
      (await api.get<AffiliatePushState>('/admin/business-stats/affiliate/push')).data,
    partnerPushSetzen: async (enabled: boolean): Promise<AffiliatePushState> =>
      (await api.put<AffiliatePushState>('/admin/business-stats/affiliate/push', { enabled })).data,
    partnerPushProbe: async (): Promise<AffiliatePushState> =>
      (await api.post<AffiliatePushState>('/admin/business-stats/affiliate/push/test')).data,

    beliebteste: async (): Promise<CyclingEvent[]> =>
      (await api.get<CyclingEvent[]>('/events/most-favorited', { params: { limit: 5000 } })).data,
    beliebtesteKonten: async (): Promise<FavoriteAccounts> =>
      (await api.get<FavoriteAccounts>('/events/most-favorited/accounts')).data,

    /** Moderator und Admin: Zahlen ohne Personenbezug. */
    nutzerZahlen: async (): Promise<UserStats> => (await api.get<UserStats>('/users/stats')).data,
    // Ab hier nur ADMIN.
    nutzerListe: async (): Promise<User[]> => (await api.get<User[]>('/users')).data,
    nutzer: async (id: number): Promise<User> => (await api.get<User>(`/users/${id}`)).data,
    nutzerAnlegen: async (data: CreateUserRequest): Promise<User> => (await api.post<User>('/users', data)).data,
    nutzerSpeichern: async (id: number, data: UpdateUserRequest): Promise<User> =>
      (await api.put<User>(`/users/${id}`, data)).data,
    nutzerLoeschen: async (id: number): Promise<void> => { await api.delete(`/users/${id}`); },
  };
}

export type AuswertungService = ReturnType<typeof createAuswertungService>;
